import { CheckCircle, ReportGmailerrorredIcon } from '@/components/icons'
import { ROUTE_DASHBOARD, ROUTE_LOGIN } from '@/constants/Routes'
import { useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { useUserVerifyEmail } from '@/services'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

/**
 * Verifies an email address from an emailed verification link (e.g. after changing the email on an
 * account). The emailed SIGN-IN link is retired -- sign-in is a 6-digit code and new accounts come
 * through the email-first signup -- so this page only verifies addresses now: it shows the result
 * and, after a moment, sends the visitor on to the dashboard (if signed in) or the login page.
 */
export const UserVerifyEmail = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { token } = useParams()
  const { token: accessToken } = useAppSelector(authSelector)

  const { mutate: verifyUserEmailAddress } = useUserVerifyEmail()

  const [verified, setVerified] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    verifyUserEmailAddress(
      { token },
      {
        onSuccess: () => setVerified('Success'),
        onError: (err: any) => {
          setVerified('Failure')
          setErrorMessage(err?.response?.data?.message || '')
        },
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!verified) return
    const timer = setTimeout(() => {
      navigate(accessToken ? ROUTE_DASHBOARD : ROUTE_LOGIN, { relative: 'path', replace: true })
    }, 3000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verified])

  if (!verified) {
    return (
      <div className="h-screen bg-[#F1F6FC] flex items-center justify-center">
        <svg
          aria-hidden="true"
          className="w-16 h-16 mr-2 text-gray-300 animate-spin fill-blue-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
      </div>
    )
  }

  return (
    <div className="h-screen bg-[#F1F6FC] flex items-center justify-center">
      <div className="w-[475px] min-h-[225px] shadow-md text-center bg-white">
        <div className="mt-5 flex flex-col justify-center items-center">
          {verified === 'Success' ? (
            <CheckCircle className="w-12 h-12 text-green" />
          ) : (
            <ReportGmailerrorredIcon className="w-12 h-12 text-red-500" />
          )}
          <p className="text-navy-900 font-bold text-xl mt-4">
            {verified === 'Success' ? t('common.congrats') : t('common.error')}
          </p>
          <p className="text-gray-400 text-sm mt-4">
            {verified === 'Success' ? t('user.emailVerifiedSuccess') : errorMessage || t('user.emailCantVerified')}
          </p>
        </div>
      </div>
    </div>
  )
}
